import FormContainer from "@client/content/controls/Form/container";
import { getRootContainerProps } from "@client/content/lib/helper";
import { withOnSubmit } from "@client/content/modules/Checkout/hooks";
import { type RootContainerProps } from "@client/content/types";
import { type FormFieldInfo, type FormHandles } from "@client/controls/Form/component";
import Hint from "@client/controls/Hint/component";
import Select from "@client/controls/Inputs/Select/component";
import { formatMoneyDisplay } from "@client/lib/format";
import { type OptionItem } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import * as React from "react";
import { type Checkout } from "../../../types";

type Props = RootContainerProps & {
  amount: number;
  accounts: Checkout.AccountOption[];
  currencyCode: string;
};

const CheckoutPaymentWallet = (props: Props) => {
  const i18n = useI18n();

  const ref = withOnSubmit<FormHandles>((data) => {
    return {
      paymentInfo: {
        accountId: data.accountId!,
        type: "Wallet"
      }
    };
  });

  const accountOptions: OptionItem<string>[] = React.useMemo(
    () =>
      props.accounts.map((x) => ({
        label: formatMoneyDisplay(x.balance, x.currencyCode),
        value: x.value
      })),
    [props.accounts]
  );

  const renderLabel = React.useCallback(
    (x: OptionItem<string>) => {
      const account = props.accounts.find((y) => y.value === x.value)!;

      return props.currencyCode === account.currencyCode ? (
        x.label
      ) : (
        <div>
          {x.label}&nbsp;
          <Hint hint={`(${formatMoneyDisplay(account.convertedBalance, account.convertBalanceCurrencyCode!)})`} />
        </div>
      );
    },
    [props.accounts, props.currencyCode]
  );

  const fields = React.useMemo(
    (): FormFieldInfo[] => [
      {
        name: "accountId",
        label: i18n.walletAccount,
        isRequired: true,
        Component: Select,
        componentOpts: {
          options: accountOptions,
          selectedLabel: renderLabel,
          renderItemLabel: renderLabel
        },
        defaultValue: props.accounts.find((x) => x.isPreferred)?.value,
        validation: (i18n, data) => {
          const account = props.accounts.find((x) => x.value === data)!;

          return account.convertedBalance! < props.amount ? i18n.errorInsufficientFunds : undefined;
        },
        qa: "Wallet account"
      }
    ],
    [props.accounts, renderLabel]
  );

  return <FormContainer {...getRootContainerProps(props)} ref={ref} fields={fields} validateOn="change" />;
};

export default CheckoutPaymentWallet;
